import axiosInstance from '../config/axios.js';

const API_BASE = '/users';

function appendUserFormValue(formData, key, value) {
  if (value === null || value === undefined) return;
  // Never send blank password; omit means "keep current"
  if (key === 'password' && String(value).trim() === '') return;
  if (typeof value === 'boolean') {
    formData.append(key, value ? 'true' : 'false');
    return;
  }
  formData.append(key, value);
}

const userService = {
  // Get all users with optional filters and pagination
  getAll: async (page = 1, perPage = 10, params = {}) => {
    const queryParams = {
      page,
      per_page: perPage,
      ...params
    };
    const response = await axiosInstance.get(API_BASE, { params: queryParams });
    return response.data;
  },

  // Get user by ID
  getById: async (userId) => {
    const response = await axiosInstance.get(`${API_BASE}/${userId}`);
    return response.data;
  },

  // Create new user
  create: async (userData, profilePhoto = null, documentAttachment = null, drivingLicenseAttachment = null) => {
    const formData = new FormData();
    
    // Append all user data
    Object.keys(userData).forEach(key => {
      appendUserFormValue(formData, key, userData[key]);
    });

    // Append files if provided
    if (profilePhoto) {
      formData.append('profile_photo', profilePhoto);
    }
    if (documentAttachment) {
      formData.append('document_attachment', documentAttachment);
    }
    if (drivingLicenseAttachment) {
      formData.append('driving_license_attachment', drivingLicenseAttachment);
    }

    const response = await axiosInstance.post(API_BASE, formData, {
      headers: {
        'Content-Type': undefined,
      },
    });
    return response.data;
  },

  // Update user
  update: async (userId, userData, profilePhoto = null, documentAttachment = null, drivingLicenseAttachment = null) => {
    const formData = new FormData();

    Object.keys(userData).forEach((key) => {
      appendUserFormValue(formData, key, userData[key]);
    });

    if (profilePhoto) {
      formData.append('profile_photo', profilePhoto);
    }
    if (documentAttachment) {
      formData.append('document_attachment', documentAttachment);
    }
    if (drivingLicenseAttachment) {
      formData.append('driving_license_attachment', drivingLicenseAttachment);
    }

    const response = await axiosInstance.put(`${API_BASE}/${userId}`, formData, {
      headers: {
        'Content-Type': undefined,
      },
    });
    return response.data;
  },

  // Delete user (soft delete)
  delete: async (userId) => {
    const response = await axiosInstance.delete(`${API_BASE}/${userId}`);
    return response.data;
  },

  // Activate user
  activate: async (userId) => {
    const response = await axiosInstance.post(`${API_BASE}/${userId}/activate`);
    return response.data;
  },

  // Get user profile photo
  getProfilePhoto: (userId) => {
    return `${axiosInstance.defaults.baseURL}${API_BASE}/${userId}/profile-photo`;
  },

  // Get user document attachment
  getDocumentAttachment: (userId) => {
    return `${axiosInstance.defaults.baseURL}${API_BASE}/${userId}/document-attachment`;
  },

  // Get user driving license attachment
  getDrivingLicenseAttachment: (userId) => {
    return `${axiosInstance.defaults.baseURL}${API_BASE}/${userId}/driving-license-attachment`;
  },

  // Get users by role
  getByRole: async (roleId) => {
    const response = await axiosInstance.get(`${API_BASE}/role/${roleId}`);
    return response.data;
  },

  // Get users by city
  getByCity: async (city) => {
    const response = await axiosInstance.get(`${API_BASE}/city/${city}`);
    return response.data;
  },

  // Send credentials email
  sendCredentials: async (userId) => {
    const response = await axiosInstance.post(`${API_BASE}/${userId}/send-credentials`);
    return response.data;
  },

  downloadBulkExport: (companyId = null) => {
    const params = {};
    if (companyId) params.company_id = companyId;
    return axiosInstance.get(`${API_BASE}/bulk-export`, { params, responseType: 'blob' });
  },

  downloadBulkTemplate: () => {
    return axiosInstance.get(`${API_BASE}/bulk-template`, { responseType: 'blob' });
  },

  uploadBulkExcel: (file, companyId = null) => {
    const formData = new FormData();
    formData.append('file', file);
    if (companyId) formData.append('company_id', companyId);
    return axiosInstance.post(`${API_BASE}/bulk-import`, formData, {
      headers: { 'Content-Type': undefined },
    });
  },
};

export default userService;